import { forwardRef, useImperativeHandle, useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import BusinessIncomeFilters from '@/components/v2/molecules/admin/tabs/BusinessIncomeTab/BusinessIncomeFilters';
import TabEmptyState from '@/components/v2/atoms/admin/TabEmptyState';
import DeleteDialog from '@/components/v2/molecules/admin/tabs/shared/DeleteDialog';
import BusinessIncomeFormModal from '@/components/v2/organisms/admin/tabs/BusinessIncomeTab/BusinessIncomeFormModal';
import BulkEditBusinessIncomeModal from '@/components/v2/organisms/admin/tabs/BusinessIncomeTab/BulkEditBusinessIncomeModal';
import BusinessIncomeTable, { PaginatedBusinessIncome } from '@/components/v2/organisms/admin/tabs/BusinessIncomeTab/BusinessIncomeTable';
import { useBusinessIncomeFilters } from '@/hooks/admin/tabs/useBusinessIncomeFilters';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import useTransactionModals from '@/hooks/admin/shared/useTransactionModals';
import { useDeleteBusinessIncome } from '@/hooks/useBusinessIncome';
import { Plus, FileX } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import TablePagination from '@/components/v2/molecules/admin/shared/TablePagination';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/utils/shared/currencyUtils';
import { useShiftSelect } from '@/hooks/useShiftSelect';

interface SelectionState {
  count: number;
  total: number;
  onDelete: () => void;
  onEdit?: () => void;
  isDeleting: boolean;
}

export interface BusinessTransactionsIncomeViewRef {
  downloadCSV: () => void;
  createNew: () => void;
  hasData: boolean;
}

interface BusinessTransactionsIncomeViewProps {
  onSelectionChange?: (selection: SelectionState | null) => void;
}

const BusinessTransactionsIncomeView = forwardRef<BusinessTransactionsIncomeViewRef, BusinessTransactionsIncomeViewProps>(
  ({ onSelectionChange }, ref) => {
    const { canCreate, canEdit, canDelete } = useRolePermissions();
    const { toast } = useToast();

    const deleteIncome = useDeleteBusinessIncome();

    const {
      itemToEdit: incomeToEdit,
      isModalOpen: isIncomeModalOpen,
      isDeleteDialogOpen: isDeleteIncomeDialogOpen,
      handleNew: handleNewIncome,
      handleEdit: handleEditIncome,
      handleModalClose: handleIncomeModalClose,
      handleDelete: handleDeleteIncome,
      confirmDelete: confirmDeleteIncome,
      setIsDeleteDialogOpen: setIsDeleteIncomeDialogOpen,
    } = useTransactionModals<PaginatedBusinessIncome>({
      onDelete: async (id) => await deleteIncome.mutateAsync(id),
    });

    const {
      paginatedIncomes,
      isLoading: isLoadingIncomes,
      error: errorIncomes,
      refetch: refetchIncomes,
      currentPage,
      pageSize,
      totalCount,
      totalPages,
      goToPage,
      changePageSize,
      searchTerm,
      setSearchTerm,
      selectedOrganizations,
      setSelectedOrganizations,
      selectedProperties,
      setSelectedProperties,
      selectedIncomeType,
      setSelectedIncomeType,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      allOrganizations,
      filteredPropertiesForCascade,
      hasActiveFilters,
      clearFilters,
      fetchAllFilteredIncomes,
    } = useBusinessIncomeFilters();

    const {
      selectedItems: selectedIncomes,
      handleSelect: handleSelectIncome,
      handleSelectAll,
      clearSelection,
    } = useShiftSelect({ items: paginatedIncomes });

    const [isDeleting, setIsDeleting] = useState(false);
    const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
    const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);

    const selectedIncomesTotal = useMemo(() => {
      if (selectedIncomes.length === 0) return 0;
      return paginatedIncomes
        .filter(income => selectedIncomes.includes(income.id))
        .reduce((sum, income) => sum + (income.total_payment || 0), 0);
    }, [selectedIncomes, paginatedIncomes]);

    const handleDownloadCSV = async () => {
      try {
        let incomesToExport: PaginatedBusinessIncome[] = [];

        if (selectedIncomes.length > 0) {
          incomesToExport = paginatedIncomes.filter(income =>
            selectedIncomes.includes(income.id)
          );
        } else {
          incomesToExport = await fetchAllFilteredIncomes();
        }

        const csvHeaders = [
          'Organización',
          'Propiedad',
          'Tipo de Ingreso',
          'Pago Bruto',
          'IVA',
          'Pago Total',
          'Descripción',
          'Estatus',
          'Tipo de Pago',
          'Fecha de Pago',
        ];

        const csvData = incomesToExport.map((income) => [
          income.organization_name || 'Sin organización',
          income.property_short_name || 'Sin propiedad',
          income.income_type || 'N/A',
          income.gross_payment?.toString() || '0',
          income.iva?.toString() || '0',
          income.total_payment?.toString() || '0',
          income.description || '',
          income.payment_status || 'N/A',
          income.payment_type || 'N/A',
          income.payment_date || 'N/A',
        ]);

        const csvContent = [
          csvHeaders.join(','),
          ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `ingresos_negocio_${new Date().toISOString().split('T')[0]}.csv`,
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: 'CSV descargado',
          description: `Se han exportado ${incomesToExport.length} ingresos`,
        });
      } catch (error) {
        console.error('Error downloading CSV:', error);
        toast({
          title: 'Error',
          description: 'No se pudo descargar el archivo CSV',
          variant: 'destructive',
        });
      }
    };

    const handleDeleteSelected = () => {
      setIsBulkDeleteDialogOpen(true);
    };

    const confirmBulkDelete = async () => {
      setIsDeleting(true);
      try {
        await Promise.all(
          selectedIncomes.map(incomeId =>
            deleteIncome.mutateAsync(incomeId)
          )
        );

        toast({
          title: "Éxito",
          description: `Se eliminaron ${selectedIncomes.length} ingreso(s) correctamente`,
        });

        clearSelection();
        setIsBulkDeleteDialogOpen(false);
      } catch (error) {
        console.error("Error deleting selected incomes:", error);
        toast({
          title: "Error",
          description: "Error al eliminar los ingresos seleccionados",
          variant: "destructive",
        });
      } finally {
        setIsDeleting(false);
      }
    };

    const handleEditSelected = () => {
      setIsBulkEditModalOpen(true);
    };

    const handleBulkEditSuccess = () => {
      clearSelection();
      setIsBulkEditModalOpen(false);
    };

    // Exponer métodos al padre
    useImperativeHandle(ref, () => ({
      downloadCSV: handleDownloadCSV,
      createNew: handleNewIncome,
      hasData: paginatedIncomes.length > 0,
    }));

    // Notify parent of selection changes
    useEffect(() => {
      if (selectedIncomes.length > 0) {
        onSelectionChange?.({
          count: selectedIncomes.length,
          total: selectedIncomesTotal,
          onDelete: handleDeleteSelected,
          onEdit: canEdit ? handleEditSelected : undefined,
          isDeleting,
        });
      } else {
        onSelectionChange?.(null);
      }
    }, [selectedIncomes.length, selectedIncomesTotal, isDeleting, canEdit, onSelectionChange]);

    return (
      <div className="space-y-6">
        {/* Filtros */}
        <BusinessIncomeFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedOrganizations={selectedOrganizations}
          setSelectedOrganizations={setSelectedOrganizations}
          selectedProperties={selectedProperties}
          setSelectedProperties={setSelectedProperties}
          selectedIncomeType={selectedIncomeType}
          setSelectedIncomeType={setSelectedIncomeType}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          organizations={allOrganizations}
          filteredPropertiesForCascade={filteredPropertiesForCascade}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
        />

        {/* Body */}
        {errorIncomes && (
          <TabErrorBanner
            title="Error al cargar ingresos"
            message="No se pudieron cargar los ingresos. Por favor, intenta nuevamente."
            onRetry={() => refetchIncomes()}
          />
        )}

        {isLoadingIncomes ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : paginatedIncomes.length === 0 ? (
          <TabEmptyState
            icon={<FileX className="h-12 w-12" />}
            title="No hay ingresos"
            description="No se encontraron ingresos que coincidan con los filtros aplicados."
            actionLabel={canCreate ? "Crear Ingreso" : undefined}
            onAction={canCreate ? handleNewIncome : undefined}
          />
        ) : (
          <div className="space-y-4">
            <BusinessIncomeTable
              incomes={paginatedIncomes}
              canEdit={canEdit}
              canDelete={canDelete}
              onDelete={handleDeleteIncome}
              onEdit={handleEditIncome}
              selectedIncomes={selectedIncomes}
              onSelectIncome={handleSelectIncome}
              onSelectAll={handleSelectAll}
            />
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
            />
          </div>
        )}

        <BusinessIncomeFormModal
          isOpen={isIncomeModalOpen}
          onClose={handleIncomeModalClose}
          editingIncome={incomeToEdit}
        />

        <DeleteDialog
          title="¿Estás seguro?"
          description="Esta acción no se puede deshacer. Se eliminará permanentemente el registro de ingreso."
          open={isDeleteIncomeDialogOpen}
          onOpenChange={setIsDeleteIncomeDialogOpen}
          onConfirm={confirmDeleteIncome}
        />

        <DeleteDialog
          title={`¿Eliminar ${selectedIncomes.length} ingreso(s)?`}
          description={`Esta acción no se puede deshacer. Se eliminarán permanentemente ${selectedIncomes.length} registro(s) de ingreso.`}
          open={isBulkDeleteDialogOpen}
          onOpenChange={setIsBulkDeleteDialogOpen}
          onConfirm={confirmBulkDelete}
          isLoading={isDeleting}
        />

        <BulkEditBusinessIncomeModal
          isOpen={isBulkEditModalOpen}
          onClose={() => setIsBulkEditModalOpen(false)}
          selectedIds={selectedIncomes}
          onSuccess={handleBulkEditSuccess}
        />
      </div>
    );
  });

BusinessTransactionsIncomeView.displayName = 'BusinessTransactionsIncomeView';

export default BusinessTransactionsIncomeView;